// src/pages/builder/DuplicateTopicsPanel.js
//
// Layout Builder ▸ 🧩 Duplicate topics — find topics saved more than once and
// merge them into one.
//
// WHY
// ---
// "Continents & Oceans" ended up as four library records: two with the SAME
// slug (continentsoceans), plus continents-oceans and continentsandoceans.
//   * Two records with one slug: every page carrying that slug reads just ONE
//     of them, and which one isn't fixed — so its video, song and sing-along
//     seem to swap between visits.
//   * Look-alike slugs: each page reads its own record, so a fix made on one
//     never reaches the pages using the other.
//
// WHAT IT FINDS
// -------------
// Records that share an exact slug, a slug that only differs in punctuation
// (continents-oceans / continentsoceans), or the same title ("&" = "and").
// For each record: its media files, game, sing-along timings and how many
// pages use its slug.
//
// WHAT "MERGE" DOES (after you confirm)
// -------------------------------------
//   1. The record you KEEP gets the video, song, sing-along, picture and
//      lyrics you picked (from any record in the group).
//   2. If you picked another record's game or sing-along timings, they are
//      copied to the kept slug (an existing game there is backed up to
//      quizBackups first).
//   3. Pages using the other slugs are switched to the kept slug. Each page
//      is backed up to layoutBackups first. If a page then lists the topic
//      twice, the second copy is removed.
//   4. The other records are set to status "merged" (hidden from every page,
//      nothing deleted — set one back to Live in the Topic library to undo),
//      or deleted if you ticked that.
//
// Reads topicLibrary, portalLayouts, quizzes, lyricSync. Writes only on Merge.

import React, { useMemo, useState } from "react";
import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc, addDoc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase/firebaseConfig";
import { clearLibraryCache, getAllLibraryTopics } from "../../services/topicLibraryService";
import { getAllLyricSyncs, mediaKey } from "../../services/lyricSyncService";
import { getLiveGames } from "../../services/gameGeneratorService";
import { exactKey, legacyKey } from "../../utils/topicKey";

const who = () => getAuth().currentUser?.email || getAuth().currentUser?.uid || "unknown";
const fileOf = (url) => (url ? decodeURIComponent(String(url).split("?")[0].split("/").pop() || "").replace(/^.*%2F/, "").split("/").pop() : "");
const titleKey = (t) =>
  String(t || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "");
const lyricCount = (l) => (Array.isArray(l) ? l : String(l || "").split("\n")).filter((x) => String(x).trim()).length;
const recId = (t) => String(t.id || t.slug || "");
const stampNow = () => new Date().toISOString().replace(/[-:]/g, "").replace(/\..*$/, "").replace("T", "-");

const MEDIA = [
  { key: "videoUrl", label: "🎬 Video" },
  { key: "musicUrl", label: "🎵 Song" },
  { key: "singAlongUrl", label: "🎤 Sing-along" },
  { key: "imageUrl", label: "🖼 Picture" },
];

/* ------------------------------------------------------------------ */
/* Finding the groups                                                  */
/* ------------------------------------------------------------------ */

const findGroups = (topics) => {
  const parent = new Map();
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const union = (a, b) => parent.set(find(a), find(b));
  const owner = new Map(); // key -> first record id
  topics.forEach((t) => parent.set(recId(t), recId(t)));
  topics.forEach((t) => {
    const keys = [`s:${exactKey(t.slug)}`, `l:${legacyKey(t.slug)}`];
    const tk = titleKey(t.title);
    if (tk.length >= 5) keys.push(`t:${tk}`);
    keys.forEach((k) => {
      if (k.length < 4) return;
      if (owner.has(k)) union(recId(t), owner.get(k));
      else owner.set(k, recId(t));
    });
  });
  const groups = new Map();
  topics.forEach((t) => {
    const g = find(recId(t));
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(t);
  });
  return [...groups.values()]
    .filter((g) => g.length > 1)
    .map((g) => {
      const slugs = g.map((t) => exactKey(t.slug));
      const sameSlug = slugs.some((s, i) => slugs.indexOf(s) !== i);
      return { records: g, sameSlug, title: g.find((t) => t.title)?.title || g[0].slug };
    })
    .sort((a, b) => Number(b.sameSlug) - Number(a.sameSlug) || String(a.title).localeCompare(String(b.title)));
};

/* ------------------------------------------------------------------ */
/* One group                                                           */
/* ------------------------------------------------------------------ */

const Group = ({ group, scan, onMerged }) => {
  const recs = group.records;
  const info = (t) => {
    const ek = exactKey(t.slug);
    const lk = legacyKey(t.slug);
    const gameKey = scan.games[ek] ? ek : scan.games[lk] ? lk : "";
    const syncKey = scan.syncs[ek] ? ek : scan.syncs[lk] ? lk : "";
    return {
      pages: scan.pages[ek]?.length || 0,
      sharedSlug: recs.some((o) => recId(o) !== recId(t) && exactKey(o.slug) === ek),
      gameKey,
      game: gameKey ? scan.games[gameKey] : null,
      syncKey,
      sync: syncKey ? scan.syncs[syncKey] : null,
    };
  };
  const filled = (t) => MEDIA.filter((m) => t[m.key]).length + (lyricCount(t.lyrics) ? 1 : 0);
  const bestKeep = useMemo(
    () =>
      [...recs].sort(
        (a, b) =>
          info(b).pages - info(a).pages ||
          Number(b.status === "published") - Number(a.status === "published") ||
          filled(b) - filled(a)
      )[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recs, scan]
  );

  const [keepId, setKeepId] = useState(recId(bestKeep));
  const keep = recs.find((t) => recId(t) === keepId) || bestKeep;
  const firstWith = (key) => (keep[key] ? recId(keep) : recId(recs.find((t) => t[key]) || keep));
  const [pick, setPick] = useState({});
  const pickOf = (key) => pick[key] ?? firstWith(key);
  const lyricsFrom = pick.lyrics ?? (lyricCount(keep.lyrics) ? recId(keep) : recId(recs.find((t) => lyricCount(t.lyrics)) || keep));
  const gameFrom = pick.game ?? (info(keep).game ? recId(keep) : recId(recs.find((t) => info(t).game) || keep));
  const syncFrom = pick.sync ?? (info(keep).sync ? recId(keep) : recId(recs.find((t) => info(t).sync) || keep));
  const [movePages, setMovePages] = useState(true);
  const [deleteOthers, setDeleteOthers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);

  const byId = (id) => recs.find((t) => recId(t) === id);
  const others = recs.filter((t) => recId(t) !== recId(keep));
  const keepSlug = exactKey(keep.slug);
  const otherSlugs = [...new Set(others.map((t) => exactKey(t.slug)).filter((s) => s !== keepSlug))];
  const pagesToMove = otherSlugs.reduce((n, s) => n + (scan.pages[s]?.length || 0), 0);
  const chosenSong = byId(pickOf("musicUrl"))?.musicUrl || "";
  const chosenSync = info(byId(syncFrom) || keep).sync;
  const syncMismatch =
    chosenSync && chosenSong && ![chosenSync.musicKey || mediaKey(chosenSync.musicUrl), chosenSync.fadedMusicKey].filter(Boolean).includes(mediaKey(chosenSong));

  const say = (line) => setLog((l) => [...l, line]);

  const merge = async () => {
    const lines = [
      `Keep “${keep.title || keep.slug}” (${keep.slug}, record ${recId(keep)}).`,
      movePages && pagesToMove ? `Switch ${pagesToMove} page entr${pagesToMove === 1 ? "y" : "ies"} from ${otherSlugs.join(", ")} to ${keep.slug} (each page is backed up first).` : "",
      `${deleteOthers ? "DELETE" : "Hide (status “merged”)"} the other ${others.length} record${others.length === 1 ? "" : "s"}.`,
    ].filter(Boolean);
    if (!window.confirm(`Merge these topics?\n\n• ${lines.join("\n• ")}`)) return;
    setBusy(true);
    setLog([]);
    try {
      const stamp = stampNow();
      // 1. the kept record
      const patch = { updatedAt: serverTimestamp(), mergedFrom: others.map(recId) };
      MEDIA.forEach((m) => {
        patch[m.key] = byId(pickOf(m.key))?.[m.key] || "";
      });
      patch.lyrics = byId(lyricsFrom)?.lyrics || keep.lyrics || [];
      if (recs.some((t) => t.status === "published")) patch.status = "published";
      await setDoc(doc(db, "topicLibrary", recId(keep)), patch, { merge: true });
      say(`✓ Kept record updated (${MEDIA.map((m) => `${m.label.split(" ")[1]}: ${fileOf(patch[m.key]) || "none"}`).join(" · ")}).`);

      // 2. game and timings onto the kept slug
      const gSrc = info(byId(gameFrom) || keep);
      if (gSrc.gameKey && gSrc.gameKey !== keepSlug) {
        const target = doc(db, "quizzes", keepSlug);
        const before = await getDoc(target);
        if (before.exists()) {
          await addDoc(collection(db, "quizBackups"), { slug: keepSlug, quiz: before.data(), reason: "replaced by Duplicate topics merge", backedUpAt: serverTimestamp(), backedUpBy: who() });
        }
        const src = await getDoc(doc(db, "quizzes", gSrc.gameKey));
        if (src.exists()) {
          await setDoc(target, { ...src.data(), slug: keepSlug, mergedFrom: gSrc.gameKey });
          say(`✓ Game copied from ${gSrc.gameKey} to ${keepSlug}${before.exists() ? " (the old one was backed up)" : ""}.`);
        }
      }
      const sSrc = info(byId(syncFrom) || keep);
      if (sSrc.syncKey && sSrc.syncKey !== keepSlug) {
        const src = await getDoc(doc(db, "lyricSync", sSrc.syncKey));
        if (src.exists()) {
          await setDoc(doc(db, "lyricSync", keepSlug), { ...src.data(), slug: keepSlug, mergedFrom: sSrc.syncKey });
          say(`✓ Sing-along timings copied from ${sSrc.syncKey} to ${keepSlug}.`);
        }
      }

      // 3. pages
      if (movePages && otherSlugs.length) {
        const wanted = new Set(otherSlugs);
        const ids = new Set(otherSlugs.flatMap((s) => scan.pages[s] || []));
        let moved = 0;
        let removed = 0;
        let ownMedia = 0; // moved entries with their own video/song typed on the page
        const pagesWithOwn = new Set();
        for (const id of ids) {
          const ref = doc(db, "portalLayouts", id);
          // eslint-disable-next-line no-await-in-loop
          const fresh = await getDoc(ref);
          if (!fresh.exists()) continue;
          const data = fresh.data();
          let changed = 0;
          const seen = new Set();
          const next = [];
          (data.topics || []).forEach((t) => {
            let slug = String(t?.slug || "");
            if (wanted.has(exactKey(slug))) {
              slug = keep.slug;
              changed += 1;
              if (t?.videoUrl || t?.video || t?.musicUrl || t?.music || t?.singAlongUrl) {
                ownMedia += 1;
                pagesWithOwn.add(id);
              }
            }
            const k = exactKey(slug);
            if (k === keepSlug && seen.has(k)) {
              removed += 1; // the page would list the same topic twice
              changed += 1;
              return;
            }
            if (k) seen.add(k);
            next.push(slug === t?.slug ? t : { ...t, slug });
          });
          if (!changed) continue;
          // eslint-disable-next-line no-await-in-loop
          await setDoc(doc(db, "layoutBackups", `${id}__${stamp}`), data);
          // eslint-disable-next-line no-await-in-loop
          await updateDoc(ref, { topics: next.map((t, i) => ({ ...t, order: i })) });
          moved += 1;
        }
        say(`✓ ${moved} page${moved === 1 ? "" : "s"} switched to ${keep.slug}${removed ? ` (${removed} repeated entr${removed === 1 ? "y" : "ies"} removed)` : ""}. Backups: layoutBackups/…__${stamp}.`);
        if (ownMedia) {
          say(
            `ℹ ${ownMedia} of those entries have their own video/song typed on the page, which still wins over the library: ${[...pagesWithOwn].slice(0, 6).join(", ")}${
              pagesWithOwn.size > 6 ? "…" : ""
            }. Clear them in the Layout Builder if those pages should show the kept media.`
          );
        }
      }

      // 4. the other records
      for (const t of others) {
        // eslint-disable-next-line no-await-in-loop
        if (deleteOthers) await deleteDoc(doc(db, "topicLibrary", recId(t)));
        // eslint-disable-next-line no-await-in-loop
        else await setDoc(doc(db, "topicLibrary", recId(t)), { status: "merged", mergedInto: recId(keep), mergedAt: serverTimestamp(), mergedBy: who() }, { merge: true });
      }
      say(`✓ ${others.length} other record${others.length === 1 ? "" : "s"} ${deleteOthers ? "deleted" : "hidden (status “merged”)"}.`);
      clearLibraryCache();
      setDone(true);
      onMerged?.();
    } catch (e) {
      say(`✗ Stopped: ${e?.code === "permission-denied" ? "permission denied — this needs an admin account" : e?.message || e}. Steps above this line were done.`);
    } finally {
      setBusy(false);
    }
  };

  // a plain function (not a component) so the selects aren't re-created on
  // every render
  const choice = (field, label) => {
    const withIt = recs.filter((t) => (field === "lyrics" ? lyricCount(t.lyrics) : t[field]));
    if (!withIt.length) return null;
    const value = field === "lyrics" ? lyricsFrom : pickOf(field);
    return (
      <label className="dt-choice">
        <span>{label}</span>
        <select value={value} onChange={(e) => setPick((p) => ({ ...p, [field]: e.target.value }))} disabled={busy || done}>
          {withIt.map((t) => (
            <option key={recId(t)} value={recId(t)}>
              {field === "lyrics" ? `${lyricCount(t.lyrics)} lines` : fileOf(t[field])} — from {t.slug}
              {recId(t) === recId(keep) ? " (kept)" : ""}
            </option>
          ))}
        </select>
      </label>
    );
  };

  return (
    <div className={`dt-group ${group.sameSlug ? "is-same" : ""} ${done ? "is-done" : ""}`}>
      <div className="dt-group-head">
        <h4>{group.title}</h4>
        {group.sameSlug ? <span className="dt-pill is-red">same slug twice — pages can swap between them</span> : <span className="dt-pill is-amber">look-alikes</span>}
        {done && <span className="dt-pill is-green">merged ✓</span>}
      </div>

      <div className="dt-scroll">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Keep</th>
              <th>Slug · record</th>
              <th>Status</th>
              <th>🎬 Video</th>
              <th>🎵 Song</th>
              <th>🎤 Sing-along</th>
              <th>Lyrics</th>
              <th>🎮 Game</th>
              <th>Timings</th>
              <th>Pages</th>
            </tr>
          </thead>
          <tbody>
            {recs.map((t) => {
              const i = info(t);
              return (
                <tr key={recId(t)} className={recId(t) === recId(keep) ? "is-keep" : ""}>
                  <td>
                    <input type="radio" name={`keep-${group.title}-${recs.map(recId).join("|")}`} checked={recId(t) === recId(keep)} disabled={busy || done} onChange={() => setKeepId(recId(t))} />
                  </td>
                  <td>
                    <code>{t.slug}</code>
                    <small> {recId(t) !== t.slug ? `id: ${recId(t)}` : ""}</small>
                  </td>
                  <td>{t.status || "—"}</td>
                  <td title={t.videoUrl}>{fileOf(t.videoUrl) || "—"}</td>
                  <td title={t.musicUrl}>{fileOf(t.musicUrl) || "—"}</td>
                  <td title={t.singAlongUrl}>{fileOf(t.singAlongUrl) || "—"}</td>
                  <td>{lyricCount(t.lyrics) || "—"}</td>
                  <td>{i.game ? `${i.game.gameType} (${i.game.count})` : "—"}</td>
                  <td>{i.sync ? (i.sync.published ? "live" : "draft") : "—"}</td>
                  <td>
                    {i.pages}
                    {i.sharedSlug ? " (shared slug)" : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!done && (
        <>
          <div className="dt-choices">
            <strong>The kept topic gets:</strong>
            {MEDIA.map((m) => (
              <React.Fragment key={m.key}>{choice(m.key, m.label)}</React.Fragment>
            ))}
            {choice("lyrics", "📝 Lyrics")}
            {recs.some((t) => info(t).game) && (
              <label className="dt-choice">
                <span>🎮 Game</span>
                <select value={gameFrom} onChange={(e) => setPick((p) => ({ ...p, game: e.target.value }))} disabled={busy}>
                  {recs
                    .filter((t) => info(t).game)
                    .map((t) => (
                      <option key={recId(t)} value={recId(t)}>
                        {info(t).game.gameType} ({info(t).game.count}) — from {t.slug}
                      </option>
                    ))}
                </select>
              </label>
            )}
            {recs.some((t) => info(t).sync) && (
              <label className="dt-choice">
                <span>⏱ Sing-along timings</span>
                <select value={syncFrom} onChange={(e) => setPick((p) => ({ ...p, sync: e.target.value }))} disabled={busy}>
                  {recs
                    .filter((t) => info(t).sync)
                    .map((t) => (
                      <option key={recId(t)} value={recId(t)}>
                        {fileOf(info(t).sync.musicUrl) || "timings"} — from {t.slug}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>
          {syncMismatch && (
            <p className="dt-warn">
              ⚠ The timings you picked were made for a different song file than the song you picked, so the highlighted words won’t show until that song is
              re-synced in the Sing-along studio (same recording uploaded again → just press Save there).
            </p>
          )}
          <div className="dt-actions">
            <label>
              <input type="checkbox" checked={movePages} onChange={(e) => setMovePages(e.target.checked)} disabled={busy} /> Switch pages using {otherSlugs.length ? otherSlugs.join(", ") : "the other slugs"} to{" "}
              <code>{keep.slug}</code> ({pagesToMove} entr{pagesToMove === 1 ? "y" : "ies"})
            </label>
            <label>
              <input type="checkbox" checked={deleteOthers} onChange={(e) => setDeleteOthers(e.target.checked)} disabled={busy} /> Delete the other records instead of hiding them
            </label>
            <button type="button" className="lb-btn" onClick={merge} disabled={busy}>
              {busy ? "Merging…" : `🧩 Merge into “${keep.slug}”`}
            </button>
          </div>
        </>
      )}
      {log.length > 0 && (
        <ul className="dt-log">
          {log.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const DuplicateTopicsPanel = () => {
  const [open, setOpen] = useState(false);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [onlySame, setOnlySame] = useState(false);

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const [topics, layoutsSnap, games, syncs] = await Promise.all([
        getAllLibraryTopics(),
        getDocs(collection(db, "portalLayouts")),
        getLiveGames().catch(() => ({})),
        getAllLyricSyncs().catch(() => ({})),
      ]);
      // slug -> page ids that list it
      const pages = {};
      layoutsSnap.forEach((d) => {
        (d.data()?.topics || []).forEach((t) => {
          const k = exactKey(t?.slug);
          if (!k) return;
          if (!pages[k]) pages[k] = [];
          if (!pages[k].includes(d.id)) pages[k].push(d.id);
        });
      });
      const syncMap = {};
      Object.entries(syncs || {}).forEach(([id, v]) => {
        syncMap[String(id).toLowerCase()] = v;
      });
      const live = topics.filter((t) => t.status !== "merged");
      setScan({ topics: live, pages, games, syncs: syncMap, groups: findGroups(live), at: new Date(), pageCount: layoutsSnap.size });
    } catch (e) {
      setError(e?.code === "permission-denied" ? "Permission denied — sign in with an admin account." : e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    if (!scan) return [];
    const q = search.trim().toLowerCase();
    return scan.groups.filter((g) => (!onlySame || g.sameSlug) && (!q || g.title.toLowerCase().includes(q) || g.records.some((t) => String(t.slug).includes(q))));
  }, [scan, search, onlySame]);

  return (
    <section className="lb-card dt-panel">
      <div className="lb-card-head">
        <span className="lb-step dt-step">🧩</span>
        <div>
          <h2>Duplicate topics</h2>
          <p className="lb-hint">
            Finds topics saved more than once (same slug, look-alike slugs, or the same title) and merges them into one, moving the pages over. Nothing changes until you
            press Merge on a group.
          </p>
        </div>
        <div className="dt-head-actions">
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          <div className="dt-bar">
            <button type="button" className="lb-btn lb-btn-sm" onClick={run} disabled={busy}>
              {busy ? "Scanning…" : scan ? "↻ Scan again" : "🔍 Find duplicates"}
            </button>
            {scan && (
              <>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find, e.g. continents" />
                <label>
                  <input type="checkbox" checked={onlySame} onChange={(e) => setOnlySame(e.target.checked)} /> Only “same slug twice”
                </label>
                <span className="dt-muted">
                  {scan.groups.length} group{scan.groups.length === 1 ? "" : "s"} ({scan.groups.filter((g) => g.sameSlug).length} with the same slug twice) · {scan.topics.length} records ·{" "}
                  {scan.pageCount} pages scanned
                </span>
              </>
            )}
          </div>
          {error && <p className="dt-warn">{error}</p>}
          {scan && groups.length === 0 && <p className="dt-muted">No duplicates found{search ? " for that search" : ""}.</p>}
          {groups.map((g) => (
            <Group key={g.records.map(recId).join("|")} group={g} scan={scan} />
          ))}
        </>
      )}

      <style>{`
        .lb-page .dt-panel .dt-step { background: #0ea5e9; color: #fff; }
        .lb-page .dt-panel .dt-head-actions { margin-left: auto; }
        .lb-page .dt-panel .dt-muted { font-size: 12.5px; color: var(--lb-ink-soft); }
        .lb-page .dt-panel .dt-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 6px 0 12px; }
        .lb-page .dt-panel .dt-bar input[type="text"], .lb-page .dt-panel .dt-bar input:not([type]) { min-width: 200px; }
        .lb-page .dt-panel .dt-bar label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--lb-ink); }
        .lb-page .dt-panel .dt-group { border: 1px solid var(--lb-line); border-left: 5px solid #f59e0b; border-radius: 12px; padding: 10px 12px; margin-bottom: 12px; background: var(--lb-tint); }
        .lb-page .dt-panel .dt-group.is-same { border-left-color: #ef4444; }
        .lb-page .dt-panel .dt-group.is-done { border-left-color: #22c55e; opacity: 0.85; }
        .lb-page .dt-panel .dt-group-head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
        .lb-page .dt-panel .dt-group-head h4 { margin: 0; font-size: 15px; color: var(--lb-ink); }
        .lb-page .dt-panel .dt-pill { padding: 2px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 800; }
        .lb-page .dt-panel .dt-pill.is-red { background: #fee2e2; color: #991b1b; }
        .lb-page .dt-panel .dt-pill.is-amber { background: #fff4d6; color: #8a5a00; }
        .lb-page .dt-panel .dt-pill.is-green { background: #dcfce7; color: #166534; }
        .lb-page .dt-panel .dt-scroll { overflow-x: auto; }
        .lb-page .dt-panel .dt-table { width: 100%; border-collapse: collapse; background: #fff; font-size: 12.5px; border-radius: 10px; overflow: hidden; }
        .lb-page .dt-panel .dt-table th, .lb-page .dt-panel .dt-table td { padding: 6px 8px; border-bottom: 1px solid var(--lb-line); text-align: left; white-space: nowrap; color: var(--lb-ink); max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
        .lb-page .dt-panel .dt-table th { font-size: 11.5px; color: var(--lb-ink-soft); background: var(--lb-tint); }
        .lb-page .dt-panel .dt-table tr.is-keep td { background: #eef6ff; font-weight: 700; }
        .lb-page .dt-panel .dt-table small { color: var(--lb-ink-soft); font-weight: 400; }
        .lb-page .dt-panel .dt-choices { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 14px; margin: 10px 0 6px; font-size: 13px; color: var(--lb-ink); }
        .lb-page .dt-panel .dt-choice { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; }
        .lb-page .dt-panel .dt-choice select { max-width: 300px; }
        .lb-page .dt-panel .dt-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; margin-top: 8px; font-size: 13px; color: var(--lb-ink); }
        .lb-page .dt-panel .dt-actions label { display: inline-flex; align-items: center; gap: 6px; }
        .lb-page .dt-panel .dt-actions .lb-btn { margin-left: auto; }
        .lb-page .dt-panel .dt-warn { margin: 6px 0; padding: 8px 10px; border-radius: 10px; background: #fff4d6; color: #7a4b00; font-size: 12.5px; font-weight: 700; }
        .lb-page .dt-panel .dt-log { margin: 8px 0 0; padding-left: 18px; font-size: 12.5px; color: var(--lb-ink); }
      `}</style>
    </section>
  );
};

export default DuplicateTopicsPanel;
